/**
 * Aufgaben von Konfigurator
 * - Simuliere eine Bestellung aufgrund der übergebenen Parameter
 * - Auswahl an Checkout-URL übergeben 
 */
import { computed, inject, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  orderApiKey,
  type OrderCreateRequest,
  type OrderDto,
  type ProductVariantDto,
} from "../../infrastructure/gfo_api/order_api";
import { priceFormatterKey } from "../../infrastructure/price_formatter";
import ErrorMessage from "../Shared/ErrorMessage";
import { appConfigProviderKey } from "../../infrastructure/app_config_provider";
import { productInfoProviderKey } from "../../infrastructure/productinfo_provider";
import InlineChecker from "../Shared/InlineChecker";
import { Home } from "lucide-vue-next";
import { PencilLine } from "lucide-vue-next";
import { Info } from "lucide-vue-next";
import { ApiValidationError } from "../../infrastructure/gfo_api/api_validation_error";

// wir verwenden hier das Template aus MainProducts
const appTheme = import.meta.env.VITE_APP_THEME;
import html_konfigurator from "../MainProducts/template.iway.html?raw";

let html = html_konfigurator;

export default {
  template: html,
  components: { ErrorMessage, InlineChecker, Home, PencilLine, Info },
  setup() {
    // Injections
    const { t } = useI18n();
    const orderApi = inject(orderApiKey);
    const priceFormatter = inject(priceFormatterKey);
    const appConfig = inject(appConfigProviderKey);
    const productInfoProvider = inject(productInfoProviderKey);

    // Local vars
    const simulatedOrder = ref<OrderDto | null>(null);
    const simulatedVariants = ref<ProductVariantDto[]>([]);
    const showCheckerWarning = ref(false); // ob beim checker-init Warnung angezeigt wird oder nicht
    const openGroups = ref<Record<string, boolean>>({});
    const initialGroupOrder = ref<string[]>(["Internet", "Telefonie", "Tv", "Mobile", "SLA"]); // Gruppennamen
    const selectedVariants = ref<ProductVariantDto[]>([]);
    const error = ref<string>("");
    const checkerVisible = ref(false);
    const checkerRef = ref<any>(null);
    const checkerWrapRef = ref<any>(null);

    // computed
    const acsOpenGroupsMap: Record<string, number>= {
      'internet' : 1,
      'tv': 6, 
      'phone': 20,
      'mobile': 30,
      'sla' : 3, 
    };
    const acsOpenGroupsCsv:string = appConfig?.open_groups_csv ?? ""
    const accessInstallationAddress = computed(() => {
      return simulatedOrder.value?.access_context?.address_label ?? "";
    });
    const checkerKeysCsv:string = appConfig?.checker_keys_csv ?? "";
    // list used in ACL on website
    
    const groups = computed(() => {
      const obj: Record<string, number> = {};
      simulatedVariants.value.filter((v) => v.product_is_main).forEach((v) => {
        const key = v.product_title;
        obj[key] = v.product_id;
      });
      const order_list = initialGroupOrder.value;
      // Schritt 2: Objekt → Array
      const entries = Object.entries(obj);
      // entries = [ ["Internet", 1], ["Tv", 6], ["Mobile", 3] ]

      // Sortieren nach order_list
      entries.sort(([titleA], [titleB]) => {
        const indexA = order_list.indexOf(titleA);
        const indexB = order_list.indexOf(titleB);

        const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
        const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

        return safeA - safeB;
      });

      return Object.fromEntries(entries)
    });
    const hasAccessInstallationAddress = computed(() => accessInstallationAddress.value != "");
    const isSelected = function (variant: ProductVariantDto) {
      return selectedVariants.value.some((v) => v.id == variant.id);
    };
    const maxUploadMB = computed(() => Math.round((simulatedOrder.value?.access_context?.max_upstream ?? 0) / 1000));
    const maxDownloadMB = computed(() => Math.round((simulatedOrder.value?.access_context?.max_downstream ?? 0) / 1000));
    const saveDisabled = computed(() => selectedVariants.value.length == 0);
    const totalNonRecurring = computed(() => {
      if (vatType.value == "inclusive") {
        return selectedVariants.value.reduce(
          (sum, v) => sum + v.price_fix_incl,
          0,
        );
      } else {
        return selectedVariants.value.reduce((sum, v) => sum + v.price_fix, 0);
      }
    });
    const totalRecurring = computed(() => {
      if (vatType.value == "inclusive") {
        return selectedVariants.value.reduce(
          (sum, v) => sum + v.price_rec_incl,
          0,
        );
      } else {
        return selectedVariants.value.reduce((sum, v) => sum + v.price_rec, 0);
      }
    });
    const vatType = computed(
      () => "inclusive",
    );

    const promotions = computed(() => {
      const orderFor = appConfig?.order_for;
      // record productId: array_of_promos
      let res: Record<number, string[]> = {};
      selectedVariants.value.forEach((v) => {
        if ("new" == orderFor) {
          res[v.product_id] = v.promotext_new.split("\n");
        } else {
          res[v.product_id] = v.promotext.split("\n");
        }
      })
      return res;
    })

    // methods
    const toggleSelect = function (variant: ProductVariantDto, openCheckerForAccess:boolean = true) {
      const alreadySelected = isSelected(variant);
      const newSelection = selectedVariants.value.filter(
        (v) => v.product_id != variant.product_id,
      );
      if (!alreadySelected) {
        newSelection.push(variant);
      }
      selectedVariants.value = newSelection;
      const hasAccess = newSelection.some( (x) => x.product_id == 1);
      if (openCheckerForAccess && hasAccess && !hasAccessInstallationAddress.value){
        openCheckerWithFocusAndWarning();
      }
    };
    
    const openChecker = () => {
      checkerVisible.value = true;
      nextTick(() => {
          const el = checkerWrapRef.value;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          // Prüfe, ob Element bereits im Viewport ist
          const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
          if (!isInViewport) {
            // Nur scrollen, wenn nicht im Viewport
            const top = rect.top + window.scrollY - 100;
            window.scrollTo({ top, behavior: "smooth" });
          }
        });
    }
    const openCheckerWithFocusAndWarning = () => {
      openChecker();
      showCheckerWarning.value = true;
      nextTick(() => {
          const el = checkerRef.value;
          if (!el) return;
          el.doFocus();
      })
    }
    const closeChecker = () => {
      showCheckerWarning.value = false;
      checkerVisible.value = false;
    }

    const handleCheckerResponse = async (evt: any) => {
      // use current simulation
      error.value = "";
      const currentSimulatedOrder = simulatedOrder.value;
      if (null == currentSimulatedOrder){
        console.log("current simulated order is null - cannot continue")
        error.value = "Unable to proceed.";
        return;
      }
      // simulate the order
      const request: OrderCreateRequest = {
        netz: evt.netz,
        order_for: currentSimulatedOrder?.order_for ?? "new",
        partner_number: currentSimulatedOrder?.partner_number?.toString() ?? null,
        username: null, // TODO
        customer_number:currentSimulatedOrder?.customer_number?.toString() ?? null,
        pv_ids: [],
        checker_result_uid: evt.checker_result_uid,
      };
      const res = await orderApi?.simulate(request);
      simulatedOrder.value = res?.order ?? null;
      simulatedVariants.value = res?.available_variants ?? [];
      closeChecker();
    }

    const variantsByProductId = function (
      productId: number,
    ): ProductVariantDto[] {
      return simulatedVariants.value.filter((v) => v.product_id == productId);
    };

    const productInfoOpen = function (productId: number) {
      productInfoProvider?.openInfoForProductId(productId, simulatedOrder.value?.access_context ?? null);
    }
    const productInfosAvailable = function (productId: number): boolean {
      return productInfoProvider?.hasInfoForProductId(productId , simulatedOrder.value?.access_context ?? null) ?? false;
    }

    const save = async function () {
      error.value = "";
      try {
        const hasAccess = selectedVariants.value.some((x) => x.product_id == 1);
        const checkerResultUid = simulatedOrder.value?.access_context?.checker_result_uid ?? null;
        if (hasAccess && checkerResultUid == null){
          throw new Error("KonfiguratorComponent_Access_No_ResultUid");
        }
        let params = {
          netz_id: simulatedOrder.value?.netz_id,
          checker_result_uid: checkerResultUid,
          pv_ids: selectedVariants.value.map((x) => x.id),
        }
        const url = new URL(appConfig?.checkout_url ?? window.location.href);
        Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(key, String(value));
        });
        window.location.href=url.toString();
     } catch (err) {
        console.log(err)
        if (err instanceof ApiValidationError) {
          error.value = err.title;
        } else if (err instanceof Error) {
          error.value = err.message;
        } else {
          error.value = String(err);
        }
      } 
    };

    onMounted(async () => {
      try {
        let netz = `${appConfig?.netz_slug}`;
        // simulate the order
        const request: OrderCreateRequest = {
          netz: netz,
          order_for: "new",
          partner_number: null,
          username: null, 
          customer_number: null,
          pv_ids: [],
          checker_result_uid: null,
        };
        const res = await orderApi?.simulate(request);
        simulatedOrder.value = res?.order ?? null;
        simulatedVariants.value = res?.available_variants ?? [];
        // if there is a current order, set the selected variants
        let initOpenGroups: Record<string, boolean> = {};
        const reverseMap = Object.fromEntries(
          Object.entries(acsOpenGroupsMap).map(([k, v]) => [v, k])
        ) as Record<number, string>;
        simulatedVariants.value.forEach((v) => {
          if (v.product_id in reverseMap){
            const key = reverseMap[v.product_id];
            if (acsOpenGroupsCsv.includes(key)){
              initOpenGroups[v.product_title] = true;
            }
          }
        })
        // Todo: alle öffnen rsp. die, die übergeben wurden
        openGroups.value = initOpenGroups;
        initialGroupOrder.value = Object.keys(initOpenGroups); // offen Gruppen zuerst
      } catch (err) {
        error.value = `${err}`;
      }
    });


    return {
      appTheme,
      accessInstallationAddress,
      checkerVisible,
      checkerKeysCsv,
      checkerRef,
      checkerWrapRef,
      error,
      groups,
      hasAccessInstallationAddress,
      isSelected,
      handleCheckerResponse,
      maxUploadMB,
      maxDownloadMB,
      openGroups,
      promotions,
      priceFormatter,
      productInfoOpen,
      productInfosAvailable,
      selectedVariants,
      totalNonRecurring,
      totalRecurring,
      vatType,
      variantsByProductId,
      save,
      saveDisabled,
      showCheckerWarning,
      toggleSelect,
      openCheckerWithFocusAndWarning,
      openChecker,
      t,
    };
  },
};
